import React, { useState, useEffect, useMemo } from 'react';
import api, { getApiBaseURL } from '@services/api';
import PortalSpinner from '@components/PortalSpinner';
import KeycloakContext, { IKeycloak } from './KeycloakContext';

interface KeycloakProviderProps {
  keycloak: IKeycloak;
  initOptions: any;
  i18next: any;
  children: React.ReactNode;
}

let keycloakInitialized = false;

const KeycloakProvider = (props: KeycloakProviderProps) => {
  const { keycloak, initOptions, i18next, children } = props;
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    if (keycloakInitialized) return;
    keycloakInitialized = true;

    keycloak.init(initOptions).then(async (auth: boolean) => {
      if (auth) {
        let language = keycloak.idTokenParsed?.locale;
        if (language) {
          if (language === 'pt-BR') language = 'pt';
          await i18next.changeLanguage(language ?? 'en');
        }

        const MIN_TOKEN_VALIDITY =
          Number(process.env.REACT_APP_MIN_TOKEN_VALIDITY) || 3 * 3600;

        api.interceptors.request.use(
          async (config: any) => {
            const newConf = config;
            newConf.baseURL = getApiBaseURL(keycloak.realm, newConf.url);
            await keycloak
              .updateToken(MIN_TOKEN_VALIDITY)
              .then(() => {
                newConf.headers.Authorization = `Bearer ${keycloak.token}`;
                setAuthenticated(true);
              })
              .catch(() => setAuthenticated(false));

            return newConf;
          },
          (error: any) => Promise.reject(error),
        );
      }
      setAuthenticated(auth);
    });
  }, [i18next, keycloak, initOptions]);

  const value = useMemo(() => ({ keycloak }), [keycloak]);

  if (!authenticated) return <PortalSpinner />;

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};

export default KeycloakProvider;
